import {
  CampaignTouchpointSummaryDTO,
  PagedModelCampaignTouchpointSummaryDTO,
  CampaignTouchpointSummaryDTOType,
  TouchpointVersionDTOStatus,
  TouchpointVersionDTOType,
  TouchpointDTO,
  TouchpointDTOType,
  TouchpointVersionDTO,
  TouchpointVersionSummaryDTO,
  PagedModelTouchpointVersionSummaryDTO,
  CommentDTO,
  CommentReplyDTO,
  PagedModelCommentDTO,
  PagedModelCommentReplyDTO,
} from "generated/schemas";
import { TouchpointType } from "types/touchpoint";
import { TouchpointStatuses } from "types";
import { PaginatedResponse } from "types/pagination";
import { mapPage, normalizePageMetadata } from "utilities/pagination";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import type { Comment, CommentReply } from "types/comments";

export type TouchpointListResult = {
  items: TouchpointAttributes[];
  totalPages: number;
  totalElements: number;
  pageSize: number;
};

export const mapDtoTypeToTouchpointType = (
  dtoType?: CampaignTouchpointSummaryDTOType | TouchpointDTOType,
): TouchpointType => {
  switch (dtoType) {
    case "LANDING_PAGE":
      return TouchpointType.LANDING_PAGE;
    case "OTHER":
      return TouchpointType.OTHER;
    case "EMAIL":
    default:
      return TouchpointType.EMAIL;
  }
};

export const mapDtoStatusToTouchpointStatus = (
  dtoStatus?: TouchpointVersionDTOStatus,
): TouchpointStatuses => {
  switch (dtoStatus) {
    case "APPROVED":
      return TouchpointStatuses.APPROVED;
    case "ARCHIVED":
      return TouchpointStatuses.ARCHIVED;
    case "ARCHIVED_DRAFT":
      return TouchpointStatuses.ARCHIVED_DRAFT;
    case "ARCHIVED_CLIENT_REVIEW":
      return TouchpointStatuses.ARCHIVED_CLIENT_REVIEW;
    case "CLIENT_REVIEW":
      return TouchpointStatuses.CLIENT_REVIEW;
    case "DELETED":
      return TouchpointStatuses.DELETED;
    case "PUBLISHED":
      return TouchpointStatuses.PUBLISHED;
    case "REJECTED":
      return TouchpointStatuses.REJECTED;
    case "SYSTEM_DRAFT":
      return TouchpointStatuses.SYSTEM_DRAFT;
    case "DRAFT":
    default:
      return TouchpointStatuses.DRAFT;
  }
};

export const mapTouchpointSummary = (
  dto: CampaignTouchpointSummaryDTO,
  clientId: string,
  campaignId: string,
): TouchpointAttributes => {
  const normalizedType = mapDtoTypeToTouchpointType(dto.type);
  const latestVersion = dto.latestVersion
    ? {
      id: dto.latestVersion.id ?? "",
      version: dto.latestVersion.version ?? 0,
      status: mapDtoStatusToTouchpointStatus(dto.latestVersion.status),
      fiscalYear: dto.latestVersion.fiscalYear ?? undefined,
      applyClientTheme: dto.latestVersion.applyClientTheme ?? false,
      aiGenerated: dto.latestVersion.aiGenerated ?? false,
    }
    : null;

  return {
    id: dto.id ?? "",
    name: dto.name ?? "Untitled touchpoint",
    type: normalizedType,
    clientId,
    campaignId,
    contentType: "",
    lastModifiedDate: dto.lastModifiedDate ?? new Date().toISOString(),
    folderId: dto.folderId ?? undefined,
    folderName: dto.folderName,
    publishedVersion: dto.publishedVersion,
    url: dto.link ?? undefined,
    status: undefined,
    description: undefined,
    version: latestVersion?.version,
    pageDescription: undefined,
    pageTitle: undefined,
    subjectLine: undefined,
    iterableTemplateId: undefined,
    iterableEmailTypeId: undefined,
    validContentType: dto.validContentType ?? true,
    latestVersion: latestVersion ?? undefined,
    batchJobExecutionStatus: dto.batchJobExecutionStatus ?? undefined,
    batchJobType: dto.batchJobType ?? undefined,
    mondayBoardLink: dto.mondayBoardLink ?? undefined,
    copyCode: dto.copyCode ?? undefined,
    populationType: dto.populationType,
    classes: dto.classes ?? undefined,
    touchpointBatchId: dto.latestVersion?.touchpointBatchId ?? undefined,
    touchpointBatchName: dto.latestVersion?.touchpointBatchName ?? undefined,
    // Default to false, not used in the summary screen
    insightsActivator: false,
    templateCreativeId: dto.templateCreativeId ?? undefined,
  };
};

export const mapTouchpointDetail = (
  dto: TouchpointDTO,
  clientId: string,
  campaignId: string,
): TouchpointAttributes => {
  const normalizedType = mapDtoTypeToTouchpointType(dto.type);

  return {
    id: dto.id ?? "",
    name: dto.name ?? "Untitled touchpoint",
    type: normalizedType,
    clientId: dto.clientId ?? clientId,
    campaignId: dto.campaignId ?? campaignId,
    contentType: dto.contentType ?? "",
    lastModifiedDate: new Date().toISOString(),
    folderId: dto.folderId ?? undefined,
    folderName: dto.folderName,
    publishedVersion: undefined,
    url: undefined,
    status: undefined,
    description: undefined,
    version: undefined,
    pageDescription: undefined,
    pageTitle: undefined,
    subjectLine: undefined,
    iterableTemplateId: undefined,
    iterableEmailTypeId: undefined,
    validContentType: dto.validContentType ?? true,
    latestVersion: undefined,
    batchJobExecutionStatus: dto.batchJobExecutionStatus ?? undefined,
    batchJobType: dto.batchJobType ?? undefined,
    mondayBoardLink: dto.mondayBoardLink ?? undefined,
    copyCode: dto.copyCode ?? undefined,
    populationType: dto.populationType,
    classes: dto.classes ?? undefined,
    insightsActivator: dto.insightsActivator,
    templateCreativeId: dto.templateCreativeId ?? undefined,
  };
};

export const normalizeTouchpointListResponse = (
  response: PagedModelCampaignTouchpointSummaryDTO,
  clientId: string,
  campaignId: string,
): TouchpointListResult => {
  const content = response.content ?? [];
  const meta = normalizePageMetadata(response.page, content.length);

  return {
    items: content.map((item) => mapTouchpointSummary(item, clientId, campaignId)),
    totalPages: meta.totalPages,
    totalElements: meta.totalElements,
    pageSize: meta.size,
  };
};

const mapDtoTypeToVersionType = (
  dtoType?: TouchpointVersionDTOType,
): TouchpointType | undefined => {
  switch (dtoType) {
    case "LANDING_PAGE":
      return TouchpointType.LANDING_PAGE;
    case "OTHER":
      return TouchpointType.OTHER;
    case "EMAIL":
      return TouchpointType.EMAIL;
    default:
      return undefined;
  }
};

/**
x * Map a touchpoint version payload to the aggregate `TouchpointVersionAttributes`
 * view used across the builder UI.
 *
 * Accepts both payloads the backend serves: `TouchpointVersionSummaryDTO` (list
 * endpoint) and `TouchpointVersionDTO`, which extends it with the content fields
 * (`data`, `preprocessedHtml`, `finalHtml`, `advanced*Snippet`). Detail-only
 * fields are simply absent on list items — callers that need content must fetch
 * the version by id.
 *
 * Neither DTO models `data` beyond `unknown` (BEE JSON output), and neither
 * declares the join columns the backend returns inline (`touchpointName`,
 * `campaignName`, `openCommentCount`, `linkParams`, `sourceType`, `themeId`) or
 * the sender fields resolved client-side (`iterableEmailTypeName`, `senders`,
 * `senderName`, `senderEmail`, `replyToEmail`).
 *
 * Strategy: spread the DTO as-is so unknown fields survive untouched, then
 * normalise the typed fields and `lockedBy` shape.
 */
export const mapTouchpointVersionDTO = (
  dto: TouchpointVersionSummaryDTO | TouchpointVersionDTO,
): TouchpointVersionAttributes => {
  const extra = dto as unknown as Record<string, unknown>;
  return {
    ...(extra as Partial<TouchpointVersionAttributes>),
    id: dto.id,
    touchpointId: dto.touchpointId,
    lockedBy: dto.lockedBy
      ? {
          id: dto.lockedBy.id,
          email: dto.lockedBy.email ?? "",
          firstName: dto.lockedBy.firstName ?? "",
          lastName: dto.lockedBy.lastName ?? "",
        }
      : null,
    status: dto.status,
    type: mapDtoTypeToVersionType(dto.type),
    data: "data" in dto ? (dto.data as TouchpointVersionAttributes["data"]) : undefined,
  };
};

export const normalizeTouchpointVersionListResponse = (
  response: PagedModelTouchpointVersionSummaryDTO,
): PaginatedResponse<TouchpointVersionAttributes> =>
  mapPage(response, mapTouchpointVersionDTO);

/**
 * Map Orval's `CommentDTO` to the frontend `Comment` shape used across the
 * comment UI. Backend returns `createdDate` as an ISO string but the frontend
 * comment context treats it as `Date`.
 */
export const mapCommentDTO = (dto: CommentDTO): Comment => ({
  id: dto.id,
  text: dto.text,
  hidden: dto.hidden,
  actionItem: dto.actionItem,
  open: dto.open,
  createdDate: dto.createdDate ? new Date(dto.createdDate) : new Date(),
  replyCount: dto.replyCount,
  user: dto.user,
  versionId: dto.versionId,
  version: dto.version,
  annotationId: dto.annotationId,
});

export const mapCommentReplyDTO = (dto: CommentReplyDTO): CommentReply => ({
  id: dto.id,
  parentCommentId: dto.parentCommentId,
  text: dto.text,
  user: dto.user,
  createdDate: dto.createdDate ? new Date(dto.createdDate) : new Date(),
});

/**
 * Normalise an Orval comment page to the internal `PaginatedResponse` shape
 * consumed by `comment-context`.
 */
export const normalizeCommentPage = (response: PagedModelCommentDTO) =>
  mapPage(response, mapCommentDTO);

export const normalizeCommentReplyPage = (response: PagedModelCommentReplyDTO) =>
  mapPage(response, mapCommentReplyDTO);





